import { useCallback, useState, useEffect } from 'react';

import { InputProps } from '@components/Input/InputProps';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { isNumeric } from '@utils/characters';
import { useDebounceValue } from '@hooks/hooks';

import { Container, StyledCheckbox, InputField } from './styled';

const useTCKN = (
  value?: string,
  isTurkishCitizen?: boolean,
  onToggle?: (value: boolean) => void
) => {
  const [hasIdentityNumber, setHasIdentityNumber] = useState(isTurkishCitizen);
  const [identityNumber, setIdentityNumber] = useState<string>('');

  const onChange = (value: string) =>
    setIdentityNumber(hasIdentityNumber ? value?.slice(0, 11) : value);

  const onHasIdentityNumberStateChange = useCallback(
    (x) => {
      setHasIdentityNumber(x);
      onToggle?.(x);
    },
    [onToggle]
  );

  useEffect(() => {
    setIdentityNumber((value ?? '').slice(0, 11));
  }, [value]);

  useEffect(() => {
    setHasIdentityNumber(isTurkishCitizen);
  }, [isTurkishCitizen]);

  return {
    mode: hasIdentityNumber ? ('active' as const) : ('inactive' as const),
    onChange,
    hasIdentityNumber,
    identityNumber,
    onHasIdentityNumberStateChange,
  };
};

export type TCKNProps = {
  onToggle?: (state: boolean) => void;
  isTurkishCitizen?: boolean;
  showInput?: boolean;
  hideCheckbox?: boolean;
  onChange?: (value: string) => void;
};

const TCKNInput: React.FC<
  InputProps & TCKNProps & { alwayseditable: boolean }
> = ({
  width,
  value,
  icon,
  suffix,
  onToggle,
  isTurkishCitizen,
  showInput = true,
  hideCheckbox,
  onChange: parentOnChange,
  alwayseditable,
  ...rest
}) => {
  const {
    mode,
    hasIdentityNumber,
    identityNumber,
    onChange,
    onHasIdentityNumberStateChange,
  } = useTCKN(value as string, isTurkishCitizen, onToggle);

  const debouncedIdentityNumber = useDebounceValue(identityNumber);

  useSkipEffectOnMount(() => {
    parentOnChange?.(
      hasIdentityNumber || alwayseditable ? debouncedIdentityNumber : ''
    );
  }, [debouncedIdentityNumber, hasIdentityNumber, alwayseditable]);

  return (
    <Container
      width={width}
      icon={icon}
      suffix={suffix}
      inputShown={showInput}
      disabled={rest.disabled}
    >
      {showInput ? (
        // TODO: Unknown typescript error (ISSUE: GSF-1611)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore:next-line
        <InputField
          width='100%'
          mode={alwayseditable ? 'active' : mode}
          disabled={alwayseditable ? false : !hasIdentityNumber}
          type={isTurkishCitizen ? 'number' : 'text'}
          value={identityNumber}
          onChange={onChange}
          onKeyDown={(e) => {
            if (isTurkishCitizen && !isNumeric(e.key)) e.preventDefault();
          }}
          onWheel={(e) => {
            (e.target as HTMLElement).blur();
          }}
          {...rest}
          inputMode={isTurkishCitizen ? 'numeric' : 'text'}
          {...(!hideCheckbox && {
            suffix: (
              <StyledCheckbox
                variant='outline'
                title='T.C. Vatandaşıyım'
                onChange={
                  rest.disabled ? undefined : onHasIdentityNumberStateChange
                }
                checked={hasIdentityNumber}
                inputShown={showInput}
                disabled={rest.disabled}
                mode='controlled'
              />
            ),
          })}
        />
      ) : null}
    </Container>
  );
};

export default TCKNInput;
